import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowUpRight, BarChart3, Cloud, Cpu, PlugZap, Radio } from "lucide-react";
import type { ReactNode } from "react";
import { PublicLayout } from "@/components/public-layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { buildLinks, buildMetaTags, getCanonical } from "@/lib/seo/meta";

export const Route = createFileRoute("/developers")({
	beforeLoad: () => {
		// Temporarily disabled - show 404 page
		throw notFound();
	},

	head: () => {
		const canonical = getCanonical("/developers");
		const meta = buildMetaTags({
			title: "Developer API",
			description:
				"Build autonomous teammates on production-ready infrastructure: sandboxed execution, streaming, MCP tools.",
			ogType: "website",
		});

		return {
			meta,
			links: buildLinks({ canonical }),
		};
	},

	component: DevelopersPage,
});

function DevelopersPage() {
	return (
		<PublicLayout className="mx-auto w-full max-w-6xl gap-24 px-4 py-16 text-foreground sm:px-6 lg:px-8">
			<HeroSection />
			<InfrastructureSection />
			<CapabilitiesSection />
			<PricingSection />
			<BuildSection />
			<FinalCtaSection />
		</PublicLayout>
	);
}

function HeroSection() {
	return (
		<section className="relative isolate overflow-hidden rounded-3xl border bg-card/90 px-6 py-16 text-center shadow-lg">
			<div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-primary/15 via-transparent to-secondary/20" />
			<Badge variant="secondary" className="mx-auto w-fit text-xs uppercase tracking-wide">
				Built for developers
			</Badge>
			<h1 className="mt-6 text-balance text-4xl font-semibold leading-tight tracking-tight text-foreground sm:text-5xl">
				Production-ready autonomous agents for every use-case
			</h1>
			<p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
				Build, customise and deploy autonomous agents in minutes — serverless, sandboxed execution,
				streaming, MCPs, full toolkit.
			</p>
			<p className="mt-4 text-base font-semibold text-primary">Save time. Ship faster.</p>
			<div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
				<Button size="lg" asChild>
					<Link to="/signup">Start building with free $5 credits</Link>
				</Button>
				<Button variant="ghost" size="lg" asChild>
					<Link to="/pricing">View pricing</Link>
				</Button>
			</div>
			<p className="mt-2 text-xs italic text-muted-foreground">*No credit card required</p>
			<p className="mx-auto mt-8 max-w-xl text-sm text-muted-foreground">
				Memory, Tools, MCP, storage, Slack integration – the ultimate agent toolbox. All wrapped in
				a production-ready backend for your agents.
			</p>
			<div className="mt-10 flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
				<span>Serverless execution</span>
				<Dot />
				<span>SOC2-ready isolation</span>
				<Dot />
				<span>Streaming responses</span>
				<Dot />
				<span>Slack + MCP tooling</span>
			</div>
		</section>
	);
}

function InfrastructureSection() {
	return (
		<section className="grid gap-10 rounded-3xl border bg-muted/30 p-8 lg:grid-cols-2">
			<div className="space-y-5">
				<h2 className="text-3xl font-semibold leading-tight text-foreground">
					Your AI-agent infrastructure. Build the product, skip the infra.
				</h2>
				<p className="text-base text-muted-foreground">
					Fully-customisable autonomous agents from day one — sandboxed execution, streaming,
					multi-tenant tools. Let your product scale without managing runtime plumbing.
				</p>
				<div className="space-y-3">
					<InfraBullet title="Sandboxed compute">
						Every run is isolated with built-in secrets management and observability.
					</InfraBullet>
					<InfraBullet title="Native MCP + tool chaining">
						Attach any MCP server, custom action, or Slack workflow at runtime.
					</InfraBullet>
					<InfraBullet title="Streaming + storage">
						Realtime tokens, memory slots, and durable storage APIs for your agents.
					</InfraBullet>
				</div>
			</div>
			<Card className="h-full">
				<CardHeader>
					<CardTitle className="text-lg">Agent runtime snapshot</CardTitle>
					<CardDescription>
						Control plane + execution plane ready for production traffic.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4 text-sm text-muted-foreground">
					<div className="grid gap-3">
						<ToolStat label="Cold start" value="<50ms" />
						<ToolStat label="Tool calls / min" value="10k+" />
						<ToolStat label="Isolation" value="Per-agent VM" />
						<ToolStat label="Audit logs" value="Streaming + export" />
					</div>
					<Separator />
					<div className="rounded-2xl bg-card/80 p-4 font-mono text-xs">
						<code className="block break-words text-primary">POST /v1/agents/:id/execute</code>
						<p className="mt-2 text-muted-foreground">
							Deploy agents via API, webhook, or scheduled triggers with zero-ops.
						</p>
					</div>
				</CardContent>
			</Card>
		</section>
	);
}

function CapabilitiesSection() {
	return (
		<section className="space-y-6">
			<div className="flex flex-col gap-4 text-center">
				<h2 className="text-3xl font-semibold">Build on a complete toolkit</h2>
				<p className="text-base text-muted-foreground">
					Everything developers need: memory, tools, MCP, storage, Slack, and more — ready the
					moment you connect your repo.
				</p>
			</div>
			<div className="grid gap-4 md:grid-cols-2">
				{FEATURES.map((feature) => {
					const Icon = feature.icon;
					return (
						<Card key={feature.title} className="h-full">
							<CardHeader className="space-y-3">
								<div className="flex items-center gap-2 text-sm text-muted-foreground">
									<Badge variant="outline">{feature.badge}</Badge>
									<Icon className="size-4" />
								</div>
								<CardTitle className="text-xl">{feature.title}</CardTitle>
								<CardDescription>{feature.description}</CardDescription>
							</CardHeader>
						</Card>
					);
				})}
			</div>
		</section>
	);
}

function PricingSection() {
	return (
		<section className="space-y-6">
			<div className="flex flex-col gap-4 text-center">
				<h2 className="text-3xl font-semibold">Simple, scalable pricing</h2>
				<p className="text-base text-muted-foreground">
					Pay only for runtime and inference. Credits let you experiment fast; scale linearly when
					you ship.
				</p>
			</div>
			<Card className="p-8">
				<div className="grid gap-8 md:grid-cols-2">
					<div>
						<h3 className="text-xl font-semibold text-foreground">Compute / runtime</h3>
						<p className="mt-3 text-sm text-muted-foreground">
							Serverless execution with per-second billing.
						</p>
						<ul className="mt-4 list-disc space-y-1 pl-5 text-sm">
							<li>$0.2304 per hour</li>
							<li>$0.000064 per second</li>
						</ul>
					</div>
					<div>
						<h3 className="text-xl font-semibold text-foreground">Inference</h3>
						<p className="mt-3 text-sm text-muted-foreground">
							Simple token-based billing across supported models.
						</p>
						<ul className="mt-4 list-disc space-y-1 pl-5 text-sm">
							<li>Token input: $4 / MTok</li>
							<li>Token output: $20 / MTok</li>
						</ul>
					</div>
				</div>
				<p className="mt-6 text-sm text-muted-foreground">
					Bring your own API key to skip inference charges (coming soon).
				</p>
			</Card>
		</section>
	);
}

function BuildSection() {
	return (
		<section className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
			<Card className="h-full">
				<CardHeader>
					<CardTitle>Launch in minutes</CardTitle>
					<CardDescription>
						Point your agent at a task, wire tools, stream results. No infra tickets required.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4 text-sm">
					<ol className="space-y-3">
						<li>
							<span className="font-semibold text-foreground">1. Create a mate.</span> Define goals,
							memory, and tool access via dashboard or API.
						</li>
						<li>
							<span className="font-semibold text-foreground">2. Attach tooling.</span> Bring MCP
							servers, Slack actions, webhooks, or custom SDK calls.
						</li>
						<li>
							<span className="font-semibold text-foreground">3. Ship everywhere.</span> Trigger via
							API, schedules, or user-initiated chats.
						</li>
					</ol>
					<div className="rounded-2xl bg-muted/40 p-4">
						<p className="text-xs uppercase tracking-wide text-muted-foreground">Observability</p>
						<ul className="mt-2 space-y-1 text-sm text-foreground">
							<li>• Structured run logs</li>
							<li>• Tool call traces</li>
							<li>• Exportable transcripts</li>
						</ul>
					</div>
				</CardContent>
			</Card>
			<Card className="h-full bg-slate-950 text-slate-50 dark:bg-white dark:text-slate-900">
				<CardHeader>
					<CardTitle className="text-lg">Serverless API</CardTitle>
					<CardDescription className="text-slate-200 dark:text-slate-600">
						Kick off executions via REST or stream via SSE.
					</CardDescription>
				</CardHeader>
				<CardContent>
					<pre className="overflow-x-auto text-sm leading-relaxed">
						{`curl -X POST https://api.m8tes.ai/v1/agents/$AGENT_ID/execute \
-H "Authorization: Bearer $API_KEY" \
-H "Content-Type: application/json" \
-d '{
  "input": "Draft release notes",
  "tools": ["slack", "github"],
  "metadata": { "release": "v2.4.1" }
}'`}
					</pre>
				</CardContent>
			</Card>
		</section>
	);
}

function FinalCtaSection() {
	return (
		<section className="rounded-3xl border bg-gradient-to-br from-primary to-primary/90 px-6 py-12 text-center text-primary-foreground shadow-lg">
			<h2 className="text-3xl font-semibold">Ready to build your next agent?</h2>
			<p className="mx-auto mt-4 max-w-2xl text-base text-primary-foreground/90">
				Get $5 in free credits, wire up your first mate, and start shipping autonomous workflows
				today.
			</p>
			<div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
				<Button size="lg" variant="secondary" asChild>
					<Link to="/signup">Start building</Link>
				</Button>
				<Button size="lg" variant="ghost" className="text-primary-foreground" asChild>
					<Link to="/pricing" className="inline-flex items-center gap-1">
						Explore pricing <ArrowUpRight className="size-4" />
					</Link>
				</Button>
			</div>
		</section>
	);
}

function InfraBullet({ title, children }: { title: string; children: ReactNode }) {
	return (
		<div>
			<p className="font-medium text-foreground">{title}</p>
			<p className="text-sm text-muted-foreground">{children}</p>
		</div>
	);
}

function ToolStat({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex items-center justify-between rounded-2xl border bg-background/80 px-4 py-3">
			<span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
			<span className="text-base font-semibold text-foreground">{value}</span>
		</div>
	);
}

function Dot() {
	return <span className="text-xl text-muted-foreground">•</span>;
}

const FEATURES = [
	{
		title: "Serverless runtimes",
		description: "Spin agents up per request with zero warmup and built-in observability.",
		badge: "GA",
		icon: Cpu,
	},
	{
		title: "MCP + custom tools",
		description: "Connect Google Ads, Slack, GitHub, or anything via MCP + SDK hooks.",
		badge: "GA",
		icon: PlugZap,
	},
	{
		title: "Streaming + webhooks",
		description: "Realtime tokens and webhook fan-out to keep users in the loop.",
		badge: "Beta",
		icon: Radio,
	},
	{
		title: "State & storage",
		description: "Durable memory, file storage, and encrypted secrets scoped to each mate.",
		badge: "GA",
		icon: Cloud,
	},
	{
		title: "Analytics & insights",
		description: "Trace every run, export audits, and tune prompts with production data.",
		badge: "Soon",
		icon: BarChart3,
	},
	{
		title: "Slack-native",
		description: "Deploy agents directly inside Slack with slash commands and threads.",
		badge: "GA",
		icon: ArrowUpRight,
	},
];
